import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { OnboardingData, INITIAL_ONBOARDING_DATA, SeniorityLevel } from '../types/onboarding';
import { StepProgressIndicator } from '../components/Onboarding/StepProgressIndicator';
import { Step1CVUpload } from '../components/Onboarding/Step1CVUpload';
import { Step2Location } from '../components/Onboarding/Step2Location';
import { Step3Seniority } from '../components/Onboarding/Step3Seniority';
import { Step4Industry } from '../components/Onboarding/Step4Industry';
import { ProgressModal } from '../components/Onboarding/ProgressModal';
import { ExitConfirmationModal } from '../components/Onboarding/ExitConfirmationModal';

const TOTAL_STEPS = 4;

export const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState<OnboardingData>(INITIAL_ONBOARDING_DATA);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [processingError, setProcessingError] = useState<string>('');
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const isBlockingRef = useRef(true); // Ref to track blocking state
  const hasAddedHistoryState = useRef(false); // Track if we've added history state

  // Prevent browser back/forward and page unload
  useEffect(() => {
    // Reset blocking ref when component mounts
    isBlockingRef.current = !isComplete;

    // Add browser warning when trying to close/refresh page
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isBlockingRef.current) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return '';
      }
    };

    // Add a history entry ONCE to enable back button interception
    if (!hasAddedHistoryState.current) {
      window.history.pushState({ onboarding: true }, '', window.location.href);
      hasAddedHistoryState.current = true;
    }

    // Listen for popstate (back/forward button)
    const handlePopState = (e: PopStateEvent) => {
      if (isBlockingRef.current) {
        // Prevent navigation by pushing state forward again
        window.history.pushState({ onboarding: true }, '', window.location.href);
        // Show confirmation modal
        setShowExitConfirmation(true);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isComplete]);

  // Update blocking ref when isComplete changes
  useEffect(() => {
    isBlockingRef.current = !isComplete;
  }, [isComplete]);

  // Handle exit confirmation - redirect to HOME PAGE
  const handleExitConfirm = useCallback(async () => {
    setShowExitConfirmation(false);
    // Disable blocking
    isBlockingRef.current = false;
    setIsComplete(true);

    // Clear pending signup data and jobId (user abandoned onboarding)
    sessionStorage.removeItem('pendingSignup');
    sessionStorage.removeItem('pendingJobId');

    // Navigate to home (no account was created)
    navigate('/');
  }, [navigate]);

  const handleExitCancel = useCallback(() => {
    setShowExitConfirmation(false);
    // Push state again to ensure we can intercept next back click
    window.history.pushState({ onboarding: true }, '', window.location.href);
  }, []);

  const handleStep1Update = (file: File | null, name: string, linkedinUrl: string) => {
    setOnboardingData((prev) => ({ ...prev, cvFile: file, name, linkedinUrl }));
  };

  const handleStep2Update = (country: string, city: string, workEligibility: boolean | null) => {
    setOnboardingData((prev) => ({ ...prev, country, city, workEligibility }));
  };

  const handleStep3Update = (seniority: SeniorityLevel, years_of_experience: number | null) => {
    setOnboardingData((prev) => ({ ...prev, seniority, years_of_experience }));
  };

  const handleStep4Update = (industry: string, role: string) => {
    setOnboardingData((prev) => ({ ...prev, industry, role }));
  };

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleComplete = async () => {
    // Show progress
    setIsProcessing(true);
    setIsComplete(false);
    setProcessingError('');

    try {
      // 1. Get pending signup credentials from sessionStorage
      const pendingSignupData = sessionStorage.getItem('pendingSignup');
      if (!pendingSignupData) {
        throw new Error('No signup credentials found. Please start from the signup page.');
      }

      const { email, password } = JSON.parse(pendingSignupData); // Removed jobId as it's no longer used for linking

      // 2. Create Supabase account with full_name from Step 1
      console.log('Creating Supabase account...');
      const { data: signupData, error: signupError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: onboardingData.name, // From Step 1
          },
        },
      });

      if (signupError) throw signupError;

      // Check if session was created (email confirmation disabled)
      if (!signupData.session) {
        throw new Error('Email confirmation is required. Please check your email and try logging in after confirmation.');
      }

      console.log('Account created successfully:', signupData.user?.id);

      // 3. Now user is authenticated, proceed with onboarding API call
      const formData = new FormData();

      // Add user data (now we have the authenticated user)
      formData.append('userData', JSON.stringify({
        email: signupData.user?.email,
        sub: signupData.user?.id,
        name: onboardingData.name,
        picture: signupData.user?.user_metadata?.avatar_url,
      }));

      // Add preferences
      formData.append('preferences', JSON.stringify({
        country: onboardingData.country,
        city: onboardingData.city,
        work_eligibility: onboardingData.workEligibility,
        linkedin_url: onboardingData.linkedinUrl,
        seniority: onboardingData.seniority,
        years_of_experience: onboardingData.years_of_experience,
        industry: onboardingData.industry,
        role: onboardingData.role,
      }));

      // Add CV file if exists
      if (onboardingData.cvFile) {
        formData.append('cvFile', onboardingData.cvFile);
      }

      // Debug logging
      console.log('=== ONBOARDING DATA ===');
      console.log('User:', signupData.user?.email);
      console.log('Name:', onboardingData.name);
      console.log('LinkedIn URL:', onboardingData.linkedinUrl);
      console.log('CV File:', onboardingData.cvFile?.name);
      console.log('Preferences:', {
        country: onboardingData.country,
        city: onboardingData.city,
        work_eligibility: onboardingData.workEligibility,
        seniority: onboardingData.seniority,
        years_of_experience: onboardingData.years_of_experience,
        industry: onboardingData.industry,
        role: onboardingData.role,
      });

      // Send to backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${apiUrl}/api/onboarding`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${signupData.session.access_token}`
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to complete onboarding.');
      }

      const result = await response.json();
      console.log('Onboarding successful:', result);

      // 4. Clear pending signup data and jobId from sessionStorage
      sessionStorage.removeItem('pendingSignup');
      sessionStorage.removeItem('pendingJobId'); // Ensure this is removed even if not used

      console.log('=======================');

      // Mark as complete
      setIsComplete(true);

      // Set flag to show welcome modal on dashboard
      sessionStorage.setItem('showWelcomeModal', 'true');

      // Navigate to dashboard where welcome modal will be shown
      handleCompleteAndNavigate();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setProcessingError(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const handleModalClose = () => {
    // Reset state on error to allow retry
    setIsProcessing(false);
    setProcessingError('');
  };

  const handleCompleteAndNavigate = () => {
    // Check for custom redirect path
    const redirectPath = sessionStorage.getItem('onboardingRedirect');
    if (redirectPath) {
      sessionStorage.removeItem('onboardingRedirect');
      navigate(redirectPath);
      return;
    }

    // Navigate to dashboard
    navigate('/dashboard');
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1CVUpload
            file={onboardingData.cvFile}
            name={onboardingData.name}
            linkedinUrl={onboardingData.linkedinUrl}
            onUpdate={handleStep1Update}
            onNext={handleNext}
          />
        );
      case 2:
        return (
          <Step2Location
            country={onboardingData.country}
            city={onboardingData.city}
            workEligibility={onboardingData.workEligibility}
            onUpdate={handleStep2Update}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 3:
        return (
          <Step3Seniority
            seniority={onboardingData.seniority}
            years_of_experience={onboardingData.years_of_experience}
            onUpdate={handleStep3Update}
            onNext={handleNext}
            onBack={handleBack}
          />
        );
      case 4:
        return (
          <Step4Industry
            industry={onboardingData.industry}
            role={onboardingData.role}
            onUpdate={handleStep4Update}
            onComplete={handleComplete}
            onBack={handleBack}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-white flex flex-col overflow-hidden">
      {/* Header with Logo and Exit Option - Fixed height */}
      <header className="flex-shrink-0 p-3 md:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <img
              src="/JobVitalLogo.png"
              alt="JobVital Logo"
              className="w-6 h-6 md:w-8 md:h-8 object-contain"
            />
            <span className="text-base md:text-xl font-bold text-gray-900">JobVital</span>
          </div>

          {/* Exit Button */}
          <button
            onClick={() => setShowExitConfirmation(true)}
            className="text-xs md:text-sm text-gray-500 hover:text-gray-700 transition-colors px-2 md:px-4 py-1.5 md:py-2 rounded-lg hover:bg-gray-100"
          >
            Exit to Home
          </button>
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 flex flex-col items-center justify-start md:justify-center px-4 md:px-6 pb-6 md:pb-12 overflow-y-auto">
        <div className="w-full max-w-2xl">
          {/* Step Progress Indicator */}
          <StepProgressIndicator currentStep={currentStep} totalSteps={TOTAL_STEPS} />

          {/* Current Step Content */}
          <div className="animate-fadeIn pb-4">{renderStep()}</div>
        </div>
      </main>

      {/* Progress Modal */}
      {isProcessing && (
        <ProgressModal
          isComplete={isComplete}
          error={processingError}
          onClose={handleModalClose}
        />
      )}

      {/* Exit Confirmation Modal */}
      <ExitConfirmationModal
        isOpen={showExitConfirmation}
        onConfirm={handleExitConfirm}
        onCancel={handleExitCancel}
      />
    </div>
  );
};

